import { Flex, Tag, Tooltip } from 'antd';
import { fontSize, primitiveColors, spacing } from '../../theming';
import icon from '../../common/styles/Icon.module.scss';
import { needsReadyReport } from './reportReadiness';
import { canChangeStatus } from './statusTransitions';
import { useTranslation } from 'react-i18next';

// why a status tag can't be picked: not a move the map allows (SC-QA-127), or the report isn't ready (SC-QA-53)
const statusTagTooltip = ({ isNotAllowed, isNotReady, reportReadiness, t }) => {
    if (isNotAllowed) return t('statusErrors.STATUS_TRANSITION_NOT_ALLOWED.title');
    if (isNotReady) return t(`reportReadiness.${reportReadiness}`);
    return '';
};

export const JobStatusTimeline = ({
    currentStatus,
    marginTop,
    statuses,
    handleStatusChange,
    isSuperAdmin,
    reportReadiness = 'ready',
    // the job's saved status: the API checks a move from it, not from a status picked here and not yet saved
    savedStatus,
}) => {
    const { t } = useTranslation(['jobs']);
    const currentStatusIndex = statuses.findIndex(status => status.name === currentStatus);

    return (
        <Flex gap={spacing[1]} style={{ marginTop, flexWrap: 'wrap' }}>
            {statuses.map((status, index) => {
                // SC-QA-127: only the moves the status map allows this user from the saved status (or back to it)
                const from = savedStatus ?? currentStatus;
                const isNotAllowed =
                    status.name !== from &&
                    status.name !== currentStatus &&
                    !canChangeStatus(from, status.name, isSuperAdmin);
                // SC-QA-53: Verification and Done wait for the report; the current status itself stays as it is
                const isNotReady =
                    needsReadyReport(status.name) && reportReadiness !== 'ready' && status.name !== currentStatus;
                const isDisabled = isNotAllowed || isNotReady;

                return (
                    <Tooltip title={statusTagTooltip({ isNotAllowed, isNotReady, reportReadiness, t })} key={status.id}>
                        <Tag
                            key={status.id}
                            onClick={() => {
                                if (!isDisabled) {
                                    handleStatusChange(status.id);
                                }
                            }}
                            style={{
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                fontWeight: 500,
                                border: 'none',
                                height: 36,
                                fontSize: fontSize.xs,
                                marginRight: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: `${spacing[1]} ${spacing[3]}`,
                                opacity: isDisabled ? 0.5 : 1,
                                backgroundColor:
                                    currentStatusIndex > index
                                        ? status.secondaryColor
                                        : currentStatusIndex < index
                                          ? primitiveColors.gray50
                                          : status.primaryColor,
                                color:
                                    currentStatusIndex > index
                                        ? status.primaryColor
                                        : currentStatusIndex < index
                                          ? primitiveColors.gray700
                                          : primitiveColors.gray0,
                            }}
                        >
                            {currentStatusIndex > index && (
                                <i
                                    className={`fi-sr-check ${icon.xxs}`}
                                    style={{ color: status.primaryColor, marginRight: spacing[1] }}
                                />
                            )}
                            {t(`jobs:statuses.${status.name}`)}
                        </Tag>
                    </Tooltip>
                );
            })}
        </Flex>
    );
};
